// Kartan työkalutilat. Kun työkalu on päällä, alapaneeli vaihtuu retken
// aloituksen tilalle ja karttanapautukset ohjautuvat työkalulle.
//
//   poi   – kohteen vierailupisteen asetus tähtäimellä (karttatarkistus, kohta 14.3)
//   route – reittilinjan piirto napauttamalla tai omasta retkestä
//   tile  – käsin lisättävä ruutu GPS-katkoksen vuoksi (kohta 5.3)
//   impossible – ruudun merkintä mahdottomaksi (ei laillisesti tai turvallisesti kerättävissä)
//   time  – aikakone

import { useEffect, useState } from "preact/hooks";
import { addPart, drawnGeometry, editingParts, selectPart } from "../lib/routeGeometry.js";

import { day, dayFromIso, kmValue, todayIso } from "../lib/format.js";
import { distanceM } from "../lib/geo.js";
import { fixMessage, placementVerification } from "../lib/gpsPlace.js";
import { fixNow } from "../lib/location.js";

const RADII = [25, 40, 60, 100, 200];

/** `inline`: nimi vasemmalla ja valinta oikealla samalla rivillä (säästää korkeutta, jotta tähtäin jää näkyviin). */
function VerificationSelect({ value, onChange, inline = false }) {
  return (
    <div class={inline ? "field field-inline" : "field"}>
      <label for="verif">Tarkistustapa</label>
      <select id="verif" value={value} onChange={(e) => onChange(e.currentTarget.value)}>
        <option value="map_checked">Karttatarkistettu</option>
        <option value="field_checked">Maastotarkistettu, kävin paikalla</option>
      </select>
    </div>
  );
}

export function PoiPlacePanel({ app, tool, mapRef, onRadius }) {
  const [verification, setVerification] = useState("map_checked");
  const [busy, setBusy] = useState(false);
  // GPS-haku: tulos ({ lat, lon, accuracy }) ja mahdollinen virhe. Haku siirtää tähtäimen paikalle,
  // tallennus käyttää aina tähtäimen kohtaa, joten käyttäjä näkee ennen tallennusta mitä tallentuu.
  const [fix, setFix] = useState(null);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState("");

  const useMyLocation = async () => {
    setLocating(true);
    setLocateError("");
    try {
      const got = await fixNow({ simulator: app.simulator, me: app.me });
      setFix(got);
      setVerification(got.stale ? "map_checked" : "field_checked"); // tuore GPS-haku on maastotarkistus, vanha sijainti ei
      mapRef.current?.setView([got.lat, got.lon], 18);
    } catch (e) {
      setFix(null);
      setLocateError(e.message);
    }
    setLocating(false);
  };

  const save = async () => {
    const c = mapRef.current.getCenter();
    setBusy(true);
    const ok = await app.saveContentCheck(
      () => app.api.setPoiLocation(tool.id, { lat: c.lat, lon: c.lng, visit_radius_m: tool.radius, verification: placementVerification(verification, fix, c) }),
      `Vierailupiste tallennettu: ${tool.name}`,
    );
    setBusy(false);
    if (ok) app.endTool();
  };

  const message = fix ? fixMessage(fix.accuracy, fix) : null;

  return (
    <section class="tool-panel" aria-label="Vierailupisteen asetus">
      <h2>{tool.name}</h2>
      {/* Ohjeen ja GPS-tuloksen tila vaihtuu samassa kohdassa: paneeli pysyy matalana, ettei se peitä tähtäintä. */}
      {message || locateError ? (
        <p class={`s gps-note${message?.poor || locateError ? " poor" : ""}`} role={locateError ? "alert" : "status"}>{locateError || message.text}</p>
      ) : (
        <p class="hint">
          Ole paikalla ja paina "Käytä sijaintiani", tai siirrä karttaa niin, että tähtäin osuu paikkaan.
          Ei radalle, yksityispihalle eikä rajavyöhykkeelle.
        </p>
      )}
      <button class="btn btn-block" disabled={locating} onClick={useMyLocation}>{locating ? "Haetaan sijaintia…" : fix ? "Hae sijainti uudelleen" : "Käytä sijaintiani"}</button>
      <div class="field">
        <span class="label" id="radius-label">Tunnistusalue</span>
        <div class="chips" role="radiogroup" aria-labelledby="radius-label">
          {RADII.map((r) => (
            <button key={r} role="radio" aria-checked={tool.radius === r} class="chip" onClick={() => onRadius(r)}>{r} m</button>
          ))}
        </div>
      </div>
      <VerificationSelect value={verification} onChange={setVerification} inline />
      <div class="tool-actions">
        <button class="btn" onClick={app.endTool}>Peruuta</button>
        <button class="btn btn-rasti" disabled={busy} onClick={save}>Tallenna piste</button>
      </div>
    </section>
  );
}

/**
 * Salaisen kohteen paikan valinta kartalta (ylläpito). Lomakkeen keskeneräiset tiedot kulkevat mukana
 * (`tool.draft`) ja palaavat lomakkeelle valitun paikan kanssa, joten mitään ei kirjoiteta uudelleen.
 */
export function HiddenPickPanel({ app, tool, mapRef }) {
  const back = (patch = {}) => {
    app.openHiddenForm({ ...tool.draft, ...patch });
    app.endTool();
  };
  return (
    <section class="tool-panel" aria-label="Salaisen kohteen paikan valinta">
      <h2>{tool.draft.name || "Uusi salainen kohde"}</h2>
      <p class="hint">Siirrä karttaa niin, että tähtäin osuu kohteen paikkaan. Ympyrä näyttää käyntialueen, jonka sisällä löytö tunnistetaan.</p>
      <div class="tool-actions">
        <button class="btn" onClick={() => back()}>Peruuta</button>
        <button
          class="btn btn-rasti"
          onClick={() => {
            const c = mapRef.current.getCenter();
            back({ lat: c.lat.toFixed(6), lon: c.lng.toFixed(6) });
          }}
        >
          Käytä tätä paikkaa
        </button>
      </div>
    </section>
  );
}

export function RouteDrawPanel({ app, tool, onUndo, onClear }) {
  const [verification, setVerification] = useState("map_checked");
  const [fromTrip, setFromTrip] = useState(false);
  const [busy, setBusy] = useState(false);
  const length = tool.coords.reduce((sum, p, i) => (i ? sum + distanceM(...tool.coords[i - 1], ...p) : 0), 0);

  const saveDrawn = async () => {
    setBusy(true);
    const ok = await app.saveContentCheck(
      () => app.api.setRouteGeometry(tool.id, { geometry: drawnGeometry(tool), verification }),
      `Reittilinja tallennettu: ${tool.name}`,
    );
    setBusy(false);
    if (ok) app.endTool();
  };

  const saveFromTrip = async (tripId) => {
    setBusy(true);
    let joined = 0;
    const ok = await app.saveContentCheck(async () => {
      const result = await app.api.routeFromTrip(tool.id, { trip_id: tripId, verification });
      joined = result.joined_gaps;
      return result;
    }, `Reittilinja tallennettu retkestä: ${tool.name}`);
    setBusy(false);
    if (ok) {
      if (joined) app.toast({ kind: "info", what: "Tarkista katkoskohdat", sub: `Retkellä oli ${joined} katkosta. Ne yhdistettiin suoralla viivalla.` });
      app.endTool();
    }
  };

  if (fromTrip) {
    return (
      <section class="tool-panel" aria-label="Reittilinja retkestä">
        <h2>Valitse retki</h2>
        <p class="hint">Reittilinjaksi tulevat retken lihasvoimaosuudet yksinkertaistettuina.</p>
        <div class="trip-pick">
          {app.trips?.length ? app.trips.slice(0, 20).map((t) => (
            <button class="item" key={t.id} disabled={busy} onClick={() => saveFromTrip(t.id)}>
              <span />
              <div><div class="t">{t.name}</div><div class="s">{dayFromIso(t.local_day)}, {kmValue(t.eligible_km)}</div></div>
              <span />
            </button>
          )) : <p class="s">Ei tallennettuja retkiä.</p>}
        </div>
        <div class="tool-actions"><button class="btn" onClick={() => setFromTrip(false)}>Takaisin piirtoon</button></div>
      </section>
    );
  }

  return (
    <section class="tool-panel" aria-label="Reittilinjan piirto">
      <h2>{tool.name}</h2>
      <div class="field">
        <label for="route-part">Muokattava reittiosa</label>
        <select id="route-part" value={tool.partIndex ?? 0} onChange={(e) => app.setTool((t) => selectPart(t, Number(e.currentTarget.value)))}>
          {editingParts(tool).map((_, i) => <option key={i} value={i}>Osa {i + 1}</option>)}
        </select>
        <button class="btn" onClick={() => app.setTool(addPart)}>Lisää erillinen osa</button>
        <p class="s">Osien väliin ei piirretä yhdysviivaa. Tyhjennetty osa jää pois tallennuksesta.</p>
      </div>
      <p class="hint">
        Napauta reitin kulkukohtia järjestyksessä. {tool.coords.length === 1 ? "1 piste" : `${tool.coords.length} pistettä`}, {kmValue(length / 1000)}.
      </p>
      <div class="tool-actions three">
        <button class="btn" disabled={!tool.coords.length} onClick={onUndo}>Kumoa piste</button>
        <button class="btn" disabled={!tool.coords.length} onClick={onClear}>Tyhjennä</button>
        <button class="btn" onClick={() => setFromTrip(true)}>Omasta retkestä</button>
      </div>
      <VerificationSelect value={verification} onChange={setVerification} />
      <div class="tool-actions">
        <button class="btn" onClick={app.endTool}>Peruuta</button>
        <button class="btn btn-rasti" disabled={busy || !editingParts(tool).some((line) => line.length >= 2) || editingParts(tool).some((line) => line.length === 1)} onClick={saveDrawn}>Tallenna linja</button>
      </div>
    </section>
  );
}

export function TilePickPanel({ app, tool }) {
  const [doneOn, setDoneOn] = useState(tool.doneOn ?? todayIso());
  const [busy, setBusy] = useState(false);
  const key = tool.key;
  const collected = key && app.tiles?.small.find((t) => t.key === key);
  const manualId = key && app.tiles?.manual_ids?.[key];
  const trip = tool.tripId && app.trips?.find((t) => t.id === tool.tripId);

  const add = async () => {
    setBusy(true);
    await app.confirm({ kind: "tile", target: key, done_on: doneOn, trip_id: tool.tripId ?? null, note: "Lisätty käsin GPS-katkoksen vuoksi" });
    setBusy(false);
    app.setTool((t) => ({ ...t, key: null }));
  };

  const revoke = async () => {
    setBusy(true);
    await app.revoke(manualId);
    setBusy(false);
    app.setTool((t) => ({ ...t, key: null }));
  };

  return (
    <section class="tool-panel" aria-label="Ruudun lisäys käsin">
      <h2>Lisää ruutu käsin</h2>
      <p class="hint">
        Merkitse vain ruutu, jossa kävit lihasvoimin, mutta GPS ei tallentanut käyntiä.
        {trip ? ` Liitetään retkeen ${trip.name}.` : ""}
      </p>
      {!key && <p class="tool-status">Napauta ruutua kartalla.</p>}
      {key && collected && !manualId && (
        <p class="tool-status">Ruutu on jo kerätty retkellä {day(collected.at)}.</p>
      )}
      {key && manualId && (
        <>
          <p class="tool-status">Tämä ruutu on lisätty käsin.</p>
          <div class="tool-actions"><button class="btn btn-danger" disabled={busy} onClick={revoke}>Peru käsin lisäys</button></div>
        </>
      )}
      {key && !collected && (
        <div class="field">
          <label for="tile-day">Käyntipäivä</label>
          <input id="tile-day" type="date" value={doneOn} max={todayIso()} onInput={(e) => setDoneOn(e.currentTarget.value)} />
        </div>
      )}
      <div class="tool-actions">
        <button class="btn" onClick={app.endTool}>Valmis</button>
        <button class="btn btn-rasti" disabled={busy || !key || Boolean(collected) || !doneOn} onClick={add}>Lisää ruutu</button>
      </div>
    </section>
  );
}

/**
 * Ruudun merkintä mahdottomaksi. Merkintä ei anna pisteitä eikä muuta kerättyjä
 * ruutuja: se vain pitää ruudun poissa ehdotuksista (puuttuvat ruudut, seuraava
 * neliö, vienti). Suuren ruudun merkintä koskee myös sen pikkuruutuja.
 */
export function ImpossiblePanel({ app, tool }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const key = tool.key;
  const big = tool.level === "bigtile";
  const awardKey = key && `${tool.level}:${key}`;
  const collected = key && (big ? app.tiles?.large : app.tiles?.small)?.some((t) => t.key === key);
  const marked = key && app.tiles?.impossible?.find((e) => e.key === awardKey);

  const finish = async (action) => {
    setBusy(true);
    const ok = await action();
    setBusy(false);
    if (ok) {
      setNote("");
      app.setTool((t) => ({ ...t, key: null }));
    }
  };

  return (
    <section class="tool-panel" aria-label="Mahdoton ruutu">
      <h2>Merkitse mahdoton ruutu</h2>
      <p class="hint">
        Ruutu, johon ei pääse laillisesti tai turvallisesti, esimerkiksi rajavyöhyke, järven keskusta tai lentokenttä.
        Merkintä ei anna pisteitä eikä muuta kerättyjä ruutuja, se vain pitää ruudun poissa ehdotuksista.
      </p>
      <div class="chips" role="radiogroup" aria-label="Ruudun koko">
        <button class="chip" role="radio" aria-checked={!big} onClick={() => app.setTool((t) => ({ ...t, level: "tile", key: null }))}>Pikkuruutu</button>
        <button class="chip" role="radio" aria-checked={big} onClick={() => app.setTool((t) => ({ ...t, level: "bigtile", key: null }))}>Suuri ruutu</button>
      </div>
      {!key && <p class="tool-status">Napauta ruutua kartalla.</p>}
      {key && collected && <p class="tool-status">Ruutu on jo kerätty, joten se ei ole mahdoton.</p>}
      {key && marked && (
        <>
          <p class="tool-status">Merkitty mahdottomaksi{marked.note ? `: ${marked.note}` : ""}.</p>
          <div class="tool-actions"><button class="btn btn-danger" disabled={busy} onClick={() => finish(() => app.unmarkImpossible(awardKey))}>Poista merkintä</button></div>
        </>
      )}
      {key && !collected && !marked && (
        <div class="field">
          <label for="imp-note">Syy (valinnainen)</label>
          <input id="imp-note" maxLength={200} placeholder="Esim. rajavyöhyke" value={note} onInput={(e) => setNote(e.currentTarget.value)} />
        </div>
      )}
      <div class="tool-actions">
        <button class="btn" onClick={app.endTool}>Valmis</button>
        <button class="btn btn-rasti" disabled={busy || !key || Boolean(collected) || Boolean(marked)} onClick={() => finish(() => app.markImpossible(awardKey, note))}>Merkitse mahdottomaksi</button>
      </div>
    </section>
  );
}


// --- Aikakone (kohta 13.3) -------------------------------------------------------

const DAY_S = 86_400;

/** Paikallisen päivän loppuhetki (Unix-sekunteina) annetulle hetkelle. */
function endOfLocalDay(t) {
  const d = new Date(t * 1000);
  d.setHours(23, 59, 59, 0);
  return d.getTime() / 1000;
}

/**
 * Liukusäädin ensimmäisestä retkestä tähän päivään. Kartta näyttää ruudut,
 * jotka oli kerätty valitun päivän loppuun mennessä.
 */
export function TimePanel({ app, tool }) {
  const now = Date.now() / 1000;
  const first = app.trips?.length ? Math.min(...app.trips.map((t) => t.started_at)) : now;
  const days = Math.max(0, Math.ceil((endOfLocalDay(now) - endOfLocalDay(first)) / DAY_S));
  const [offset, setOffset] = useState(days); // päiviä ensimmäisestä retkestä
  const until = endOfLocalDay(first) + offset * DAY_S;

  // Haetaan ruudut pienellä viiveellä, ettei säätimen vetäminen tulvi palvelinta.
  useEffect(() => {
    const id = setTimeout(async () => {
      try {
        const tiles = await app.api.tiles(Math.round(until), app.mapModes);
        app.setTool((t) => (t?.type === "time" ? { ...t, tiles, until } : t));
      } catch (e) {
        app.toast({ kind: "info", what: "Ruutuja ei saatu", sub: e.message });
      }
    }, 200);
    return () => clearTimeout(id);
  }, [offset]);

  return (
    <section class="tool-panel time-panel" aria-label="Aikakone">
      <h2>Aikakone</h2>
      <p class="hint">Näe, miltä tutkimuskarttasi näytti valittuna päivänä.</p>
      <div class="time-label" aria-live="polite">
        {day(until)}: {tool.tiles ? `${tool.tiles.small.length} ruutua` : "ladataan…"}
      </div>
      <label class="sr-only" for="time-slider">Päivä</label>
      <input id="time-slider" type="range" min="0" max={days} step="1" value={offset} disabled={!days}
        onInput={(e) => setOffset(Number(e.currentTarget.value))} />
      {!days && <p class="s">Aikakone avautuu, kun retkiä on useammalta päivältä.</p>}
      <div class="tool-actions"><button class="btn" onClick={() => setOffset(days)}>Tänään</button><button class="btn btn-primary" onClick={app.endTool}>Valmis</button></div>
    </section>
  );
}

